import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { areEqual, deepCopy, DwComponent, DwComponentType, DwConfigService, DwContainerOptions, DwContextService, DwEventService, DwExpressionService, DwExpressionStateService, DwFormContextMode, DwFormSectionComponent, DwMetaDataFormApi, DwMetaDataFormSectionStateService, DwMetaDataFormStateService, DwMetadataFormValidationBuilderService, DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwOrmDataServiceToken, DwSectionBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken } from '@devwareapps/devware-cap';
import { AcsWrittenResultEntity, AcsWrittenTestStatusAllItems, AppMetaDataItemNames, StudentCourseForContextQueryEntity } from '../../../../meta-data/app-meta-data.service';
import { StudentCourseEndorsementContext } from '../../../courses/contexts/student-course-endorsement-context.service';
import { StudentRepositoryService } from '../../services/student-repository.service';
import { DwPubSubEventTopics } from 'src/app/features/z-devware/pubsub/models/dw-pub-sub-event-topics.model';
import { DwPubSubService } from 'src/app/features/z-devware/pubsub/dw-pub-sub-service';
import { ACSSubmissionUtilService } from '../../services/acs-submission-util.service';
import { of } from 'rxjs';
import { distinctUntilChanged, filter, finalize, switchMap, take } from 'rxjs/operators';

interface AcsWrittenEmptyStateViewModel {
  title: string;
  message: string;
  buttonText: string;
  processing: boolean;
}

@DwComponent({
  key: 'courseAcsWrittenResults',
  name: 'Course ACS Written Results',
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.Course,
  componentType: DwComponentType.formSection
})
@DwComponent({
  key: 'courseAcsWrittenResults',
  name: 'Course ACS Written Results',
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.Course,
  componentType: DwComponentType.formSection
})
@Component({
  selector: 'app-course-acs-written-results',
  templateUrl: './course-acs-written-results.component.html',
  styleUrls: ['./course-acs-written-results.component.scss']
})
export class CourseAcsWrittenResultsComponent extends DwSectionBaseComponent {
  studentCourseContext: StudentCourseForContextQueryEntity;
  acsWrittenResults: AcsWrittenResultEntity[];
  formApi: DwMetaDataFormApi;
  item = AppMetaDataItemNames.AcsWrittenResult;
  formMode = DwFormContextMode.edit;

  selectedAcsWrittenResult: AcsWrittenResultEntity;

  hideSelectedResult = false;

  showProcessingMessage: boolean;

  showFailed: boolean;
  isUploadingAcsResult = false;
  lastResultFailed = false;
  showAcsWrittenSection = false;
  acsEmptyState: AcsWrittenEmptyStateViewModel;
  private acsResultsLoadRequestId = 0;
  
  containerOptions : DwContainerOptions= {
     containerHeaderBackgroundColor: 'dw-gray-bg',
     showTitle: true,
     useContainerStyling: true,
     containerHeaderIcon : 'fa fa-code',
     containerInnerClass: 'dw-form-section-container-enhanced-inner-padding',
  }

  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
    private dwFormStateService: DwMetaDataFormStateService,
   
    private studentRepositoryService: StudentRepositoryService,
    private dwPubSubService: DwPubSubService,
    private ACSSubmissionUtilService: ACSSubmissionUtilService,
    private dwEventService: DwEventService,
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);

    this.formApi = this.dwFormStateService.state.formApi;

    this.dwFormStateService.state.formContext.contextMode = DwFormContextMode.view;
  }

  ngOnInit(): void {
    this.runAfterContextDataLoaded(StudentCourseEndorsementContext, true, (contextData) => this.initStudentCourseContext(contextData));

    const contextData = this.formApi.getContextDataValue<StudentCourseForContextQueryEntity>(StudentCourseEndorsementContext);

    if (!contextData) {
      this.subscriptions.push(
        this.formApi.getContextData(StudentCourseEndorsementContext)
          .pipe(take(1))
          .subscribe(initialContextData => {
            this.initStudentCourseContext(initialContextData);
          })
      );
    }

    this.setupEventHandlers();
  }
  setupEventHandlers(): void {
    // Refresh result and status-dependent UI for local ACS saves/deletes,
    // Student Course changes, and server-side ACS processing notifications.
    this.subscriptions.push(
      this.dwEventService.getEventData(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.AcsWrittenResult)).subscribe(() => {
        this.loadAcsWrittenResults();
      })
    );

    this.subscriptions.push(
      this.dwEventService.getEventData(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.StudentCourse)).subscribe(() => {
        this.updateStudentCourseContext();
      })
    );

    this.subscriptions.push(
      this.dwEventService.getEventData(DwPubSubEventTopics.messageReceived(ACSSubmissionUtilService.ACS_WRITTEN_RESULT_TOPIC)).subscribe(() => {
        this.loadAcsWrittenResults();
      })
    );
  }

  initStudentCourseContext(contextData: StudentCourseForContextQueryEntity): void {
    this.setStudentCourseContext(contextData);

    this.subscriptions.push(
      this.formApi.getContextData(StudentCourseEndorsementContext)
        .pipe(
          filter((data: StudentCourseForContextQueryEntity) => !!data?.StudentCourseId),
          distinctUntilChanged((previous, current) => areEqual(previous, current))
        )
        .subscribe(updatedContextData => {
          this.setStudentCourseContext(updatedContextData);
          this.loadAcsWrittenResults();
        })
    );

    if (this.studentCourseContext?.StudentCourseId) {
      this.loadAcsWrittenResults();
    }
  }

  onAcsWrittenResultChange(acsWrittenResult: AcsWrittenResultEntity): void {
    this.selectedAcsWrittenResult = acsWrittenResult;

    this.hideSelectedResult = true;

    setTimeout(() => {
      this.hideSelectedResult = false;
    }, 100);
  }

  uploadAcsResults(files: FileList | null, fileControl: HTMLInputElement): void {
    const studentCourseId = this.studentCourseContext?.StudentCourseId;
    const courseAcsTestId = this.studentCourseContext?.CourseAcsTestId;

    if (!files?.length || !studentCourseId) {
      fileControl.value = '';
      return;
    }

    this.isUploadingAcsResult = true;
    this.lastResultFailed = false;
    this.updateEmptyState();

    const uploadSubscription = this.ACSSubmissionUtilService.showSelectFilesModal(files, fileControl)
      .pipe(
        switchMap(media => media
          ? this.ACSSubmissionUtilService.processACSTestFile(
            media,
            studentCourseId,
            courseAcsTestId
          )
          : of(null)
        ),
        finalize(() => {
          this.isUploadingAcsResult = false;
          fileControl.value = '';
          this.updateEmptyState();
        })
      )
      .subscribe(result => {
        if (result) {
          this.lastResultFailed = result.AcsWrittenTestStatusId === AcsWrittenTestStatusAllItems.FailedInvalid;
          this.updateEmptyState();
          this.loadAcsWrittenResults();
        }
      });

    this.subscriptions.push(uploadSubscription);
  }

   loadAcsWrittenResults(): void {
    if (!this.studentCourseContext?.StudentCourseId) {
      return;
    }

    const loadRequestId = ++this.acsResultsLoadRequestId;

    this.studentRepositoryService.getAcsWrittenResults(this.studentCourseContext.StudentCourseId)
      .subscribe(results => {
        if (loadRequestId !== this.acsResultsLoadRequestId) {
          return;
        }

        this.acsWrittenResults = results;

        this.selectedAcsWrittenResult = this.acsWrittenResults.length > 0 ? this.acsWrittenResults[0] : null;

        if (this.selectedAcsWrittenResult) {
          this.showProcessingMessage = false;
          this.showFailed = false;

          switch (this.selectedAcsWrittenResult.AcsWrittenTestStatusId) {
            case AcsWrittenTestStatusAllItems.New:
            case AcsWrittenTestStatusAllItems.Processing:

              this.showProcessingMessage = true;
              break;
            case AcsWrittenTestStatusAllItems.FailedInvalid:
              this.showFailed = true;
              break;
          }
        } else {
          this.showProcessingMessage = false;
          this.showFailed = false;
        }

        this.updateEmptyState();
        this.acsWrittenResults = results.filter(r => r.AcsWrittenResultId == this.selectedAcsWrittenResult?.AcsWrittenResultId || r.AcsWrittenTestStatusId != AcsWrittenTestStatusAllItems.FailedInvalid);

        this.updateStudentCourseContext();
      });
  }

  updateStudentCourseContext() {
    this.studentRepositoryService.getStudentCourseForContext(this.studentCourseContext.StudentCourseId)
      .subscribe(context => {
        if (areEqual(context, this.studentCourseContext)) {
          return;
        }

        this.setStudentCourseContext(context);

        this.formApi.patchContextData(StudentCourseEndorsementContext, context);
      });
  }

  private setStudentCourseContext(context: StudentCourseForContextQueryEntity): void {
    this.studentCourseContext = context;
    this.showAcsWrittenSection = context?.IsEndorsed === true;
  }

  private updateEmptyState(): void {
    const processing = this.isUploadingAcsResult || this.showProcessingMessage;

    if (processing) {
      this.acsEmptyState = {
        title: 'Processing ACS written results',
        message: 'Your ACS written test is being processed. Results will appear here when processing is complete.',
        buttonText: 'Processing results',
        processing: true
      };
      return;
    }

    if (this.lastResultFailed) {
      this.acsEmptyState = {
        title: 'ACS written processing failed',
        message: 'We could not process the uploaded file. Verify the document is clear and upload it again.',
        buttonText: 'Reupload results',
        processing: false
      };
      return;
    }

    this.acsEmptyState = {
      title: 'No ACS written results',
      message: 'No ACS written results have been uploaded yet.',
      buttonText: 'Upload results',
      processing: false
    };
  }
}
